"use client"

import { useState, useEffect } from "react"
import { Clock, Stethoscope, Brain, Heart, FileText, ImageIcon, Shield, ChevronRight } from "lucide-react"
import ovejero from "../assets/images/ovejero.webp"
import logo2 from "../assets/images/logo2.svg"
import "../assets/styles/MainPage.css"
import { useTranslation } from "react-i18next"

const LandingPage = () => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState({
    benefits: false,
    features: false,
    howItWorks: false,
    testimonials: false,
    cta: false,
  })

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["benefits", "features", "howItWorks", "testimonials", "cta"]

      sections.forEach((section) => {
        const element = document.getElementById(section)
        if (element) {
          const rect = element.getBoundingClientRect()
          const isInView = rect.top <= window.innerHeight * 0.75

          setIsVisible((prev) => ({
            ...prev,
            [section]: isInView,
          }))
        }
      })
    }

    window.addEventListener("scroll", handleScroll)
    // Trigger once on load
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const benefits = [
    {
      icon: <Clock className="benefit-icon" />,
      text: t("landingPage.benefits.saveTime"),
    },
    {
      icon: <Stethoscope className="benefit-icon" />,
      text: t("landingPage.benefits.preciseDiagnosis"),
    },
    {
      icon: <FileText className="benefit-icon" />,
      text: t("landingPage.benefits.generateReports"),
    },
    {
      icon: <Heart className="benefit-icon" />,
      text: t("landingPage.benefits.morePatients"),
    },
    {
      icon: <Brain className="benefit-icon" />,
      text: t("landingPage.benefits.complexCases"),
    },
    {
      icon: <Shield className="benefit-icon" />,
      text: t("landingPage.benefits.dataProtection"),
    },
  ]

  const features = [
    {
      icon: <ImageIcon className="feature-icon" />,
      title: t("landingPage.features.imageAnalysis.title"),
      description: t("landingPage.features.imageAnalysis.description"),
    },
    {
      icon: <FileText className="feature-icon" />,
      title: t("landingPage.features.observations.title"),
      description: t("landingPage.features.observations.description"),
    },
    {
      icon: <Brain className="feature-icon" />,
      title: t("landingPage.features.aiDiagnosis.title"),
      description: t("landingPage.features.aiDiagnosis.description"),
    },
    {
      icon: <Heart className="feature-icon" />,
      title: t("landingPage.features.pdfReports.title"),
      description: t("landingPage.features.pdfReports.description"),
    },
  ]

  const steps = [
    {
      number: "01",
      title: t("landingPage.howItWorks.step1.title"),
      description: t("landingPage.howItWorks.step1.description"),
    },
    {
      number: "02",
      title: t("landingPage.howItWorks.step2.title"),
      description: t("landingPage.howItWorks.step2.description"),
    },
    {
      number: "03",
      title: t("landingPage.howItWorks.step3.title"),
      description: t("landingPage.howItWorks.step3.description"),
    },
    {
      number: "04",
      title: t("landingPage.howItWorks.step4.title"),
      description: t("landingPage.howItWorks.step4.description"),
    },
  ]

 /*const testimonials = [
    {
      quote: t("landingPage.testimonials.quote1"),
      author: t("landingPage.testimonials.author1"),
      role: t("landingPage.testimonials.role1"),
    },
    {
      quote: t("landingPage.testimonials.quote2"),
      author: t("landingPage.testimonials.author2"),
      role: t("landingPage.testimonials.role2"),
    },
  ]*/

  return (
    <div className="landing-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-grid">
            {/* Content Side */}
            <div className="hero-text">
              <div className="logo-container">
                <img src={logo2 || "/placeholder.svg"} alt="DiagnovetAI Logo" className="brand-logo" />
              </div>

              <h1 className="hero-title animate-fade-in">
                {t("landingPage.perfectTool")} <span className="text-primary">{t("landingPage.modernVets")}</span>
              </h1>

              <p className="hero-description animate-fade-in-delay">{t("landingPage.description")}</p>

              <div className="hero-buttons animate-fade-in-delay-2">
                <a
                  href="https://forms.gle/hUqN62UEtfw7XD1F6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  {t("landingPage.registerHere")}
                </a>
                {/*<button className="btn btn-secondary">{t("landingPage.viewDemo")}</button>*/}
              </div>
            </div>

            {/* Image Side */}
            <div className="hero-image">
              <div className="image-container animate-float">
                <div className="image-glow"></div>
                <img src={ovejero || "/placeholder.svg"} alt={t("landingPage.dogAlt")} className="dog-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className={`benefits-section ${isVisible.benefits ? "visible" : ""}`}>
        <div className="benefits-content">
          <div className="benefits-header">
            <h2 className="section-title">{t("landingPage.assistant")}</h2>
            <p className="section-description">{t("landingPage.assistantDescription")}</p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="benefit-icon-container">{benefit.icon}</div>
                <p className="benefit-text">{benefit.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={`features-section ${isVisible.features ? "visible" : ""}`}>
        <div className="features-content">
          <div className="features-header">
            <h2 className="section-title">{t("landingPage.featuresTitle")}</h2>
            <p className="section-description">{t("landingPage.featuresDescription")}</p>
          </div>

          <div className="features-grid">
            {features.slice(0, 3).map((feature, index) => (
              <div key={index} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon-container">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Special highlighted feature card */}
          <div className="feature-highlight" style={{ animationDelay: "0.3s" }}>
            <div className="feature-highlight-content">
              <div className="feature-highlight-icon">{features[3].icon}</div>
              <div className="feature-highlight-text">
                <h3 className="feature-highlight-title">{features[3].title}</h3>
                <p className="feature-highlight-description">{features[3].description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="howItWorks" className={`how-it-works-section ${isVisible.howItWorks ? "visible" : ""}`}>
        <div className="how-it-works-content">
          <div className="how-it-works-header">
            <h2 className="section-title">{t("landingPage.howItWorksTitle")}</h2>
            <p className="section-description">{t("landingPage.howItWorksDescription")}</p>
          </div>

          <div className="steps-container">
            {steps.map((step, index) => (
              <div key={index} className="step-card" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < steps.length - 1 && <ChevronRight className="step-arrow" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Currently Disabled
      <section id="testimonials" className={`testimonials-section ${isVisible.testimonials ? "visible" : ""}`}>
        <div className="testimonials-content">
          <div className="testimonials-header">
            <h2 className="section-title">{t("landingPage.testimonialsTitle")}</h2>
            <p className="section-description">{t("landingPage.testimonialsDescription")}</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="quote-mark">"</div>
                <p className="testimonial-quote">{testimonial.quote}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <Award className="award-icon" />
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{testimonial.author}</h4>
                    <p className="author-role">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* CTA Section */}
      <section id="cta" className={`cta-section ${isVisible.cta ? "visible" : ""}`}>
        <div className="cta-content">
          <h2 className="cta-title">{t("landingPage.readyToTransform")}</h2>
          <p className="cta-description">{t("landingPage.joinVeterinarians")}</p>
          <a
            href="https://forms.gle/hUqN62UEtfw7XD1F6"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-cta"
          >
            {t("landingPage.startNow")}
          </a>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
